import { Row } from '@tanstack/react-table';
import { useMemo } from 'react';
import { GroupType } from 'src/WebApiController';

/*
  Hook to adapt TanStack table rows into grouped structure.
  Identifies parent group rows and associates their child rows accordingly.
  Skips certain rows based on business logic (e.g., Active listing groups at depth 1).
*/
export function useGroupedRows<T>(
  tableRows: Row<T>[],
  isGroupRow?: (data: T) => boolean
) {
  return useMemo(() => {
    const ungroupedRows: typeof tableRows = [];
    const groupsMap = new Map<string, { parent: Row<T>; children: Row<T>[] }>();

    // Build row lookup map for O(1) access instead of O(n) find
    const rowsById = new Map<string, Row<T>>();
    tableRows.forEach((row) => {
      rowsById.set(row.id, row);
    });

    // Process rows
    tableRows.forEach((row) => {
      if (row.depth === 0) {
        if (isGroupRow?.(row.original)) {
          // Top-level parent group
          groupsMap.set(row.id, { parent: row, children: [] });
        } else {
          // Ungrouped row
          ungroupedRows.push(row);
        }
      } else if (row.depth > 0 && row.parentId) {
        // Child row - walk up to find top-level parent
        let current = row;
        let topLevelParentId: string | null = null;

        while (current.depth > 0 && current.parentId) {
          const parent = rowsById.get(current.parentId);
          if (!parent) break;

          if (parent.depth === 0) {
            topLevelParentId = parent.id;
            break;
          }
          current = parent;
        }

        // Add to parent's children if found
        if (topLevelParentId) {
          // Skip Active listing groups that are direct children (depth 1) of top-level parent
          const listing = (row.original as any)?.listing;
          const isActiveGroupAtDepth1 =
            row.depth === 1 &&
            listing?.isLtGrp &&
            listing?.groupType === GroupType.Active;

          if (!isActiveGroupAtDepth1) {
            groupsMap.get(topLevelParentId)?.children.push(row);
          }
        }
      }
    });
    const adaptedRows = [...ungroupedRows];

    Array.from(groupsMap.values()).forEach(
      (group: { parent: Row<T>; children: Row<T>[] }) => {
        adaptedRows.push(group.parent); // Add parent first
        adaptedRows.push(...group.children);
      }
    );

    return adaptedRows;
  }, [tableRows, isGroupRow]);
}
